import {
  LayerType,
  isNullOrUndefined,
  getIngredientTypeName,
  getIngredientTypeColor,
} from './../../../utils/Global';
import {
  LayerTarget,
  TargetLayer,
  TargetLayerIngredient,
} from './../../../business/models/CustomModels';
import {
  ScreenLayerDTO,
  IngredientClient,
  LayerDTO,
  PlateClient,
  WellIngDTO,
} from './../../../business/models/Models';
import ExperimentDtoClient from './../../../business/models/ExperimentDtoClient';
import {
  createTargetLayer,
  CreateTargetLayerIngredient,
  findWellsInLayer,
  getIngIngType,
  getIngredient,
  getIngStock,
  IsBuffer,
  RemoveWellIngredientsForOverrideLayer,
  updateDropByTargetLayer,
  updateWellByTargetLayer,
  getIngredientType,
} from './GenerateCommon';
import { Compute } from './VolCalculator';
import ModelFactory from 'business/models/ModelFactory';

export async function ScreenLayerGenerate(docData: ExperimentDtoClient, lyr: LayerDTO) {
  let screenLyr: ScreenLayerDTO;
  if (lyr.type != LayerType.Screen) {
    throw new Error('ScreenLayerGenerate: Invalid Layer Type.');
  } else {
    screenLyr = lyr.layerDetail as ScreenLayerDTO;
  }

  const layerHeight = lyr.height + 1;
  const layerWidth = lyr.width + 1;
  const wells: WellIngDTO[][] = findWellsInLayer(
    docData.plateDto,
    lyr,
    docData.containerRows,
    docData.containerCols,
  );

  if (isNullOrUndefined(screenLyr.screenLot)) {
    return;
  }

  const sourceWells = getSourceScreenWells(screenLyr);
  let sourceWellsIndex = 0;
  let conditionNumber = 1;

  for (let i = 0; i < layerHeight; ) {
    for (let j = 0; j < layerWidth; ) {
      const well = wells[i][j];
      RemoveWellIngredientsForOverrideLayer(docData, lyr, well);

      // leave the well if no source well found.
      if (sourceWellsIndex >= sourceWells.length) {
        // Source screen has no more conditions
        break;
      }
      const srcWell = sourceWells[sourceWellsIndex];
      if (
        srcWell == null ||
        (srcWell.wellVolume == 0 && srcWell.wellLayerList.length == 0)
      ) {
        sourceWellsIndex++;
        continue;
      }

      well.wellVolume += lyr.volume;

      const targetLayer = createTargetLayer(lyr, well);
      targetLayer.volume = lyr.volume;
      targetLayer.waterVolume = 0;
      targetLayer.sourceWellId = sourceWells[sourceWellsIndex].wellId;
      targetLayer.sourceWellConditionNumber = conditionNumber + ' ';
      const maxSourceWellConditionNumberLength = 50;
      if (
        targetLayer.sourceWellConditionNumber.length > maxSourceWellConditionNumberLength
      ) {
        targetLayer.sourceWellConditionNumber =
          targetLayer.sourceWellConditionNumber.substring(
            0,
            maxSourceWellConditionNumberLength,
          );
      }
      conditionNumber++;

      GenerateWellLayerIngredients(
        docData,
        well,
        sourceWells[sourceWellsIndex],
        targetLayer,
      );

      Compute(targetLayer, lyr, docData);

      if (lyr.targetDrop < 0) {
        updateWellByTargetLayer(lyr, targetLayer, well);
        well.wellWaterVolume += targetLayer.waterVolume;
      } else {
        const drop = updateDropByTargetLayer(lyr, targetLayer, well);
        drop.volume += targetLayer.volume;
        drop.waterVolume += targetLayer.waterVolume;
      }

      sourceWellsIndex++;
      j++;
      if (sourceWellsIndex >= sourceWells.length) {
        // Source screen has no more conditions
        break;
      }
    }
    i++;
  }
}

export function GetLayerTarget(targetWell: WellIngDTO, layer: LayerDTO): LayerTarget {
  let target: LayerTarget;
  if (layer.targetDrop >= 0) {
    if (targetWell.dropList === undefined) {
      throw 'Target drop No. not found exception';
    }

    target = targetWell.dropList.find((d) => d.dropNumber === layer.targetDrop);

    if (target === undefined) {
      throw 'Target drop No. not found exception';
    }
  } else if (layer.targetDrop < 0) {
    target = targetWell;
  }
  return target;
}

export async function GetSourceScreen(
  screenId,
  defaultWellVolume,
  containerMaxWellVolume,
  lotNumber,
) {
  const plateService: PlateClient = ModelFactory(PlateClient);
  const screenData = await plateService.getScreen(screenId, lotNumber);
  if (screenData === null) {
    return null;
  }
  const result: any = screenData;
  result.plates = [];

  return Promise.all(
    screenData.plateDto.map(async (plate) => {
      const ingServ: IngredientClient = ModelFactory(IngredientClient);
      const plateIngredientVM = await ingServ.getPlateIngredients(plate.plateId);
      if (plateIngredientVM === null) {
        return null;
      }
      result.plates.push(plateIngredientVM);
    }),
  ).then((values) => {
    return result;
  });
}

function GenerateWellLayerIngredients(
  docData: ExperimentDtoClient,
  wellRow: WellIngDTO,
  sourceWellRow: WellIngDTO,
  targetLayer: TargetLayer,
) {
  for (const wellLayerRow of sourceWellRow.wellLayerList) {
    for (const wellLayerIngredientRow of wellLayerRow.wellLayerIngredientList) {
      const targetLayerIngredient: TargetLayerIngredient =
        CreateTargetLayerIngredient(targetLayer);
      targetLayerIngredient.ingredientIngredientTypeId =
        wellLayerIngredientRow.ingredientIngredientTypeId;
      targetLayerIngredient.ingredientStockId = wellLayerIngredientRow.ingredientStockId;
      targetLayerIngredient.ingredientStock = getIngStock(
        wellLayerIngredientRow.ingredientStockId,
      );
      targetLayerIngredient.ingredientStockIngredient = getIngredient(
        getIngStock(wellLayerIngredientRow.ingredientStockId).ingredientId,
      );
      targetLayerIngredient.preMixedConcentration = 0;
      const ingIngType = getIngIngType(wellLayerIngredientRow.ingredientIngredientTypeId);
      targetLayerIngredient.ingredientTypeId = ingIngType.ingredientTypeId;
      const ingType = getIngredientType(ingIngType.ingredientTypeId);
      const ingTypeColor = getIngredientTypeColor(ingType.ingredientTypeColorId);
      targetLayerIngredient.ingredientTypeColorGroupId = ingTypeColor.groupId;

      targetLayerIngredient.ingredientTypeName = getIngredientTypeName(
        wellLayerIngredientRow.ingredientIngredientTypeId,
      );

      //Refactor: Remove the following line
      targetLayerIngredient.concentration = 0;
      if (
        targetLayerIngredient.preMixedConcentration !=
        wellLayerIngredientRow.concentration
      ) {
        targetLayerIngredient.preMixedConcentration =
          wellLayerIngredientRow.concentration;
      }

      if (IsBuffer(wellLayerIngredientRow.ingredientIngredientTypeId)) {
        targetLayerIngredient.isBuffer = wellLayerIngredientRow.isBuffer;
        targetLayerIngredient.ingredientTypeIsBuffer =
          wellLayerIngredientRow.ingredientTypeIsBuffer;
        targetLayerIngredient.ingredientIngredientTypeIsBuffer =
          wellLayerIngredientRow.ingredientIngredientTypeIsBuffer;

        targetLayerIngredient.pH = wellLayerIngredientRow.pH;
        targetLayerIngredient.lowPHVolume = wellLayerIngredientRow.lowPHVolume;
        targetLayerIngredient.highPHVolume = wellLayerIngredientRow.highPHVolume;

        // May be converting a well ingredient row from a single vial buffer to a dual vial.
        // Need to update the row, but only dirty it if absolutely necessary.
        if (wellLayerIngredientRow.highPhIngStock == null) {
          if (targetLayerIngredient.highPhIngStock != null) {
            targetLayerIngredient.highPhIngStock = null;
          }
        } else {
          if (
            targetLayerIngredient.highPhIngStock == null ||
            targetLayerIngredient.highPhIngStock != wellLayerIngredientRow.highPhIngStock
          ) {
            targetLayerIngredient.highPhIngStockId =
              wellLayerIngredientRow.highPhIngStockId;
          }
        }
      }
    }
  }
}
function getSourceScreenWells(screenLyr: ScreenLayerDTO) {
  const sourceScreenWells: WellIngDTO[] = [];
  const sourceExp = screenLyr.screenLot.experiment;
  const rows = sourceExp.containerRows * sourceExp.rowCount;
  const cols = sourceExp.containerCols * sourceExp.colCount;

  for (let rowIndex = 0; rowIndex < rows; rowIndex++) {
    for (let colIndex = 0; colIndex < cols; colIndex++) {
      const rowPos = Math.floor(rowIndex / sourceExp.containerRows) + 1;
      const colPos = Math.floor(colIndex / sourceExp.containerCols) + 1;
      const wellIndex =
        (rowIndex % sourceExp.containerRows) * sourceExp.containerCols +
        (colIndex % sourceExp.containerCols);
      const plate = sourceExp.plateDto.find(
        (p) => p.rowPos === rowPos && p.colPos === colPos,
      );
      if (plate) {
        const well = plate.ingredients.wellList[wellIndex];
        sourceScreenWells.push(well);
      }
    }
  }
  return sourceScreenWells;
}
